import { Injectable } from '@angular/core';
import { RequestTokenParams } from '../models/request-token-params.model';
import { Headers, Http, Response } from '@angular/http';
import { AuthenticatedUser } from '../models/authenticated-user.model';
import { Observable } from 'rxjs/Observable';
import 'rxjs/add/observable/forkJoin';
import 'rxjs/add/operator/map';
import { AuthenticatedUserRepositoryService } from '../../payment/services/authenticated-user-repository.service';
import { NavigationEnd, Router } from '@angular/router';
import { Subject } from 'rxjs/Subject';
import {UrlService} from './url.service';
import { CommonHttpService } from './http.service';
import { StoreConfigurationsService } from './store-configurations.service';

@Injectable()
export class OauthService {
    private lsIndex: string;

    private requestingTokenSource: Subject<null> = new Subject();
    private tokenGrantedSource: Subject<string> = new Subject();
    private tokenRefusedSource: Subject<null> = new Subject();
    private loadingPersistedUserSource: Subject<null> = new Subject();
    private loadingAuthenticatedUserSource: Subject<null> = new Subject();
    private loadedPersistedUserSource: Subject<any> = new Subject();
    private loadedAuthenticatedUserSource: Subject<any> = new Subject();
    private failedPersistedUserSource: Subject<any> = new Subject();
    private failedAuthenticatedUserSource: Subject<any> = new Subject();
    protected user: AuthenticatedUser;
    public requestingToken$ = this.requestingTokenSource.asObservable();
    public tokenGranted$ = this.tokenGrantedSource.asObservable();
    public tokenRefused$ = this.tokenRefusedSource.asObservable();
    public loadingPersistedUser$ = this.loadingPersistedUserSource.asObservable();
    public loadingAuthenticatedUser$ = this.loadingAuthenticatedUserSource.asObservable();
    public loadedPersistedUser$ = this.loadedPersistedUserSource.asObservable();
    public loadedAuthenticatedUser$ = this.loadedAuthenticatedUserSource.asObservable();
    public failedPersistedUser$ = this.failedPersistedUserSource.asObservable();
    public failedAuthenticatedUser$ = this.failedAuthenticatedUserSource.asObservable();

    constructor(
        protected http: Http,
        protected authenticatedUserRepository: AuthenticatedUserRepositoryService,
        protected router: Router,
        protected urlService: UrlService
    ) {
        this.router.events.subscribe(event => {
            if (event instanceof NavigationEnd) {
                this.flushServerDataStorage();
            }
        });
        this.lsIndex = window.btoa('canopus' + window.location.hostname);
    }

    /**
     * This is responsible for load authenticated user data using persisted
     * token if has. If not has a persisted token, loads authenticated user
     * data from session.
     **/
    public init() {
        if (this.hasToken()) {
            return this.authenticatedUser().subscribe((user) => {
                this.authenticatedUserRepository.setUser(user);
            });
        }
        if (!this.hasToken()) {
            return this.persistedLoggedUser().subscribe((user) => {
                this.authenticatedUserRepository.setUser(user);
            });
        }
    }

    /**
     * This requests token to API to be used in another authenticated requests.
     **/
    public requestToken(
        params?: RequestTokenParams,
        route: string = '/oauth/token'
    ) {
        this.requestingTokenSource.next(null);
        return this.http
            .post(this.urlService.build(route), params)
            .map((res: Response) => {
                this.setToken(res.json().access_token);
                this.tokenGrantedSource.next(this.getToken());

                return new Observable(observable => {
                    this.persistLogin()
                        .subscribe(() => {
                            this.authenticatedUser()
                                .subscribe(user => {
                                    observable.next(user);
                                    observable.complete();
                                });
                        });
                });
            });
    }
    /**
     * 'token' property setter and Http service's 'token' property setter.
     **/
    public setToken(token: string): void {
        const data = this.loadLocalStorageData();
        data.token = token;
        window.localStorage.setItem(this.lsIndex, JSON.stringify(data));
    }
    /**
     * 'token' property getter.
     **/
    public getToken(): string {
        const lsData = this.loadLocalStorageData();
        return lsData.token;
    }
    /**
     * This removes token from session.
     **/
    public resetToken() {
        this.forgetLogin().subscribe(_ => {
            if (this.hasToken()) {
                const data = this.loadLocalStorageData();
                delete data.token;
                window.localStorage.setItem(this.lsIndex, JSON.stringify(data));
            }
        });
    }
    /**
     * This verify if has 'CheckoutToken' session.
     **/
    public hasToken(): boolean {
        const lsData = this.loadLocalStorageData();
        return lsData.token !== undefined;
    }

    /**
     * Load store data from local storage index.
     **/
    private loadLocalStorageData(): any {
        return JSON.parse(window.localStorage.getItem(this.lsIndex) || '{}');
    }

    /**
     * This call API to persist login at backend session.
     **/
    private persistLogin() {
        return this.http
            .post(
                this.urlService.build('/ecommerce/identification/persist-login'),
                {},
                {headers: new Headers({
                        'Content-Type': 'application/json',
                        'X-Requested-With': 'XMLHttpRequest',
                        'Authorization': `Bearer ${this.getToken()}`
                    })}
            )
            .map(res => res.json());
    }
    /**
     * This call API to forget login at backend session.
     **/
    private forgetLogin() {
        return this.http
            .post(
                this.urlService.build('/ecommerce/identification/forget-login'),
                {},
                {headers: new Headers({
                        'Content-Type': 'application/json',
                        'X-Requested-With': 'XMLHttpRequest',
                        'Authorization': `Bearer ${this.getToken()}`
                    })}
            );
    }
    /**
     * This loads authenticated user data.
     **/
    private authenticatedUser() {
        this.loadingAuthenticatedUserSource.next(null);
        return this.http
            .get(
                this.urlService.build('/ecommerce/identification/authenticated-user'),
                {
                    headers: new Headers({
                        'Content-Type': 'application/json',
                        'X-Requested-With': 'XMLHttpRequest',
                        'Authorization': `Bearer ${this.getToken()}`
                    })
                }
            )
            .map((res: Response) => {
                this.user = res.json().data;
                this.loadedAuthenticatedUserSource.next(this.user);
                return res.json().data;
            }, (error: Response) => {
                this.failedAuthenticatedUserSource.next(error);
            });
    }
    /**
     * This loads authenticated user data.
     **/
    public persistedLoggedUser() {
        const params = new RequestTokenParams();
        params.grant_type = 'persisted';

        this.loadingPersistedUserSource.next();
        return new Observable(observable => {
            this.requestToken(
                params,
                'ecommerce/identification/authenticated-user'
            ).subscribe((requests) => {
                this.loadedPersistedUserSource.next();
                requests.subscribe(user => {
                    observable.next(user);
                    observable.complete();
                });
            }, (error: Response) => {
                this.failedPersistedUserSource.next(error);
                this.triggerTokenRefused();
            });
        });
    }
    /**
     * This trigger tokenRefused$ event source.
     **/
    public triggerTokenRefused() {
        this.tokenRefusedSource.next(null);
    }

    /**
     * Flush server data storage from canopus.js
     **/
    private flushServerDataStorage() {
        const storageServerDataKey = window.btoa(
            'xAHLzqELkWGEgETZ6fFXy6q72mZcbwZ8XAY6gLGU' + window.location.hostname
        );
        window.sessionStorage.removeItem(storageServerDataKey);

        const dueDateServerDataKey = window.btoa(
            'XKHXYGyrHdkKpYz39BNPExwgfdv7W8vfaK4Ud7F7' + window.location.hostname
        );
        window.sessionStorage.removeItem(dueDateServerDataKey);
    }
}



// WEBPACK FOOTER //
// /codebuild/output/src2032928750/src/src/app/shared/services/oauth.service.ts